import {
  ComparatorContext,
  useCurrentStepIndex,
  useField,
  usePage,
  useWizardService,
} from '@kizi/comparator-core';
import _ from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const useProductSorting = (
  productsWithReviews,
  currentSorting,
  showAllProducts,
  sortingReference,
  sortingDescendingOptions,
) => {
  const { state, setState } = usePage();
  const { api } = useContext(ComparatorContext);
  const sortedProducts = useMemo(() => {
    return api.sortProducts(
      productsWithReviews,
      sortingReference[currentSorting],
      sortingDescendingOptions[currentSorting],
    );
  }, [
    productsWithReviews,
    currentSorting,
    sortingReference,
    sortingDescendingOptions,
  ]);

  const finalSortedProducts = useMemo(() => {
    if (showAllProducts === undefined || showAllProducts?.value === 'true') {
      return sortedProducts;
    }

    if (showAllProducts?.value === 'false') {
      const firstNonOrderableProduct = sortedProducts.find(
        (product) => product.orderType === 'NOT_ORDERABLE',
      );

      const restSortingReferences = sortingReference.filter(
        (ref) => ref !== sortingReference[currentSorting],
      );

      return _.orderBy(
        [
          ...(firstNonOrderableProduct &&
          firstNonOrderableProduct?.[sortingReference[currentSorting]] <
            sortedProducts[1]?.[sortingReference[currentSorting]]
            ? [firstNonOrderableProduct]
            : []),
          ...sortedProducts.filter((product) => product.purchasableProduct),
        ],
        [
          sortingReference[currentSorting],
          ...(sortingReference[currentSorting] !== 'totalInstallmentAmount'
            ? restSortingReferences
            : ['totalInstallmentAmount']),
        ],
        sortingDescendingOptions[currentSorting] ? 'desc' : 'asc',
      );
    }

    return sortedProducts;
  }, [
    sortedProducts,
    showAllProducts,
    currentSorting,
    sortingReference,
    sortingDescendingOptions,
  ]);

  useEffect(() => {
    if (setState && state) {
      setState({
        ...state,
        productList: finalSortedProducts,
      });
    }
  }, [finalSortedProducts]);

  return finalSortedProducts;
};

const getComparatorStepLabel = (name, stepId) => {
  if (stepId === 'startWidget') {
    return 'Widget';
  } else if (stepId === 'overview') {
    return 'Vergelijken';
  } else {
    return '';
  }
};

export const useFieldTracking = () => {
  const { name } = useContext(ComparatorContext);
  const wizard = useWizardService();
  const currentStepIndex = useCurrentStepIndex() ?? -1;
  const interpreter = wizard?.state?.context?.steps?.[currentStepIndex];
  const stepId = interpreter?.id;
  const stepLabel = getComparatorStepLabel(name, stepId);

  const privateFields = useMemo(
    () => ['postcode', 'housenumber', 'housenumberaddition'],
    [],
  );

  const isPrivateField = useCallback(
    (fieldName) => {
      return privateFields.includes(fieldName.toLowerCase());
    },
    [privateFields],
  );

  const trackInteraction = useCallback(
    (event, customFieldName = null) => {
      const { name: fieldName, value, localName, checked } = event.target;
      const interactionType = event.type;
      const ga4eventType = event.ga4event ? event.ga4event : 'form_usage';
      if (name !== 'os-triple-play') {
        return;
      }

      const trackedFieldName = customFieldName || fieldName;
      const trackedValue = isPrivateField(trackedFieldName) ? 'private' : value;

      const googleAnalyticsObject = {
        ...(ga4eventType !== 'filter_usage'
          ? {
              event: ga4eventType,
              form_name: stepLabel,
              form_field: trackedFieldName,
              form_value: trackedValue,
              form_status:
                localName === 'checkbox'
                  ? checked
                    ? 'checked'
                    : 'unchecked'
                  : interactionType,
              form_type: localName,
            }
          : {
              event: ga4eventType,
              filter_name: trackedFieldName,
              filter_value: trackedValue,
              filter_checked:
                localName === 'checkbox'
                  ? checked
                    ? 'checked'
                    : 'unchecked'
                  : interactionType,
            }),
      };

      console.log('Form interaction:', googleAnalyticsObject);
      window?.dataLayer?.push(googleAnalyticsObject);
    },
    [name, stepLabel, isPrivateField],
  );

  const enhanceInputProps = useCallback(
    (props) => {
      const enhancedProps = { ...props };
      const eventTypes = ['onFocus', 'onBlur', 'onChange', 'onTouchStart'];

      eventTypes.forEach((eventType) => {
        const originalHandler = enhancedProps[eventType];
        enhancedProps[eventType] = (event) => {
          if (originalHandler) {
            originalHandler(event);
          }
          trackInteraction({
            ...event,
            type: eventType.toLowerCase().slice(2),
          });
        };
      });

      return enhancedProps;
    },
    [trackInteraction],
  );

  const trackEvent = useCallback(
    (fieldName) => (event) => {
      trackInteraction({ ...event, type: event.type || 'click' }, fieldName);
    },
    [trackInteraction],
  );

  return {
    enhanceInputProps,
    trackEvent,
  };
};

const tabLabels = ['Pakket informatie', 'Kosten', 'Over je aanvraag'];

export const useTabTracking = () => {
  const { name } = useContext(ComparatorContext);
  const comparator = name === 'os-triple-play' ? 'TriplePlay' : '';

  const trackTabClick = useCallback(
    (tabIndex, productType) => {
      const tabLabel = tabLabels[tabIndex];
      if (name !== 'os-triple-play') {
        return;
      }
      const googleAnalyticsObject = {
        event: 'tabs_usage',
        event_action: 'Click',
        event_label: tabLabel,
        event_category: `Funnel - ${comparator}`,
        component: productType,
      };

      window?.dataLayer?.push(googleAnalyticsObject);
    },
    [name],
  );

  return {
    trackTabClick,
  };
};

export const useSortTracking = () => {
  const { name } = useContext(ComparatorContext);

  const trackSort = useCallback(
    (option) => {
      if (name !== 'os-triple-play') {
        return;
      }
      const googleAnalyticsObject = {
        event: 'select_sorting_options',
        sorting_option: option,
      };

      // console.log('Sort interaction:', googleAnalyticsObject);
      window?.dataLayer?.push(googleAnalyticsObject);
    },
    [name],
  );

  return {
    trackSort,
  };
};
export const useTooltipTracking = () => {
  const { name } = useContext(ComparatorContext);
  const comparator = name === 'os-triple-play' ? '3P' : '';

  const trackTooltipClick = useCallback(
    (field) => {
      if (name !== 'os-triple-play') {
        return;
      }
      const googleAnalyticsObject = {
        event: 'tooltip_usage',
        event_action: 'Tooltip click',
        event_label: field,
        event_category: `Funnel - ${comparator}`,
      };

      // console.log('Tooltip interaction:', googleAnalyticsObject);
      window?.dataLayer?.push(googleAnalyticsObject);
    },
    [name],
  );

  return {
    trackTooltipClick,
  };
};

export default useTabTracking;

export const useCompareTracking = () => {
  const { name } = useContext(ComparatorContext);
  const comparator = name === 'os-triple-play' ? '3P' : '';

  const trackCompareClick = useCallback(
    (product, component) => {
      if (name !== 'os-triple-play') {
        return;
      }
      const googleAnalyticsObject = {
        event: 'compare_product',
        event_action: product.compare ? 'Checked' : 'Unchecked',
        event_label: 'Vergelijk',

        item_name: product.subscriptionName,
        item_brand: product.provider.name,
        event_category: `Funnel - ${comparator}`,
        comparison_id: product.comparisonId,
        component,
      };

      // console.log('Compare interaction:', googleAnalyticsObject);
      window?.dataLayer?.push(googleAnalyticsObject);
    },
    [name],
  );

  return {
    trackCompareClick,
  };
};

export const usePriceMode = (options = {}) => {
  const { defaultMode = 'month' } = options;
  const [priceMode, setPriceMode] = useState(defaultMode);

  const filtersField = useField('overview.filters');

  useEffect(() => {
    if (filtersField?.value) {
      try {
        const parsedFilters = JSON.parse(filtersField.value || '{}');

        if (
          parsedFilters.showPriceMode &&
          parsedFilters.showPriceMode.length > 0
        ) {
          setPriceMode(parsedFilters.showPriceMode[0]);
        }
      } catch (error) {
        console.error('Error parsing filters:', error);
      }
    }
  }, [filtersField?.value]);

  return priceMode;
};
